<script setup>
/**
 * ConfirmActionDialog.vue
 *
 * Confirmation dialog for actions whose request runs while the dialog stays
 * open (delete, purge, reset…). Built on PrimeVue `<Dialog modal>`: a header
 * with an optional Material icon and the title, the message, an optional
 * default slot, and a Cancel / Confirm footer.
 *
 * WHEN TO USE: PrimeVue's `useConfirm()` closes the dialog as soon as the user
 * accepts, so it can't show a spinner on Confirm or block closing while the
 * request runs. Use `useConfirm()` for confirmations with no async state; use
 * this component when the dialog must stay open and locked until the request
 * finishes.
 *
 * FLOW: Confirm emits `confirm` ONLY and does not close the dialog. The parent
 * sets `loading` while its request runs, then sets `visible` to false when it
 * succeeds (or keeps the dialog open and shows an error in the slot).
 * Cancel, the header X, Escape and a mask click emit `cancel` and
 * `update:visible` (false).
 *
 * LOADING LOCK: while `loading` is true, Confirm shows its spinner and is
 * always disabled (whatever `confirmProps.disabled` says), Cancel is disabled,
 * the X is hidden (`closable`), Escape and mask clicks are ignored
 * (`closeOnEscape`, `dismissableMask`), and both handlers drop any confirm or
 * close request that still gets through. Close requests are also dropped once
 * `visible` is false (during the leave transition), so `cancel` fires once.
 *
 * ACCESSIBILITY: rendered as `role="alertdialog"` (as PrimeVue's own
 * ConfirmDialog does), named by the title (`aria-labelledby`) and described by
 * the message (`aria-describedby`).
 *
 * INITIAL FOCUS: for `severity="danger"` focus goes to Cancel, so Enter right
 * after opening never runs the destructive action; for 'warn' / 'primary' it
 * goes to Confirm. If Confirm starts disabled through `confirmProps.disabled`
 * (typed confirmation), neither footer button takes focus, so an `autofocus`
 * element in the default slot gets it (the Dialog checks footer, header, then
 * content; with no slot `autofocus` it falls back to the X).
 *
 * The component only translates its fallback button labels (`common.confirm`,
 * `common.cancel`): `title`, `message` and custom labels must arrive already
 * translated. Attributes such as `id` fall through to the Dialog root.
 *
 * @example — delete with the in-dialog loading state; the parent closes on success
 * <ConfirmActionDialog
 *   v-model:visible="showDeleteModal"
 *   :title="$t('botEditor.flows.deleteModalTitle')"
 *   :message="$t('botEditor.flows.deleteModalMessage', { name: flowToDelete?.name })"
 *   :confirm-label="$t('common.delete')"
 *   icon="delete"
 *   :loading="isDeleting"
 *   :confirm-props="{ id: 'btn-confirm-delete-flow' }"
 *   :cancel-props="{ id: 'btn-cancel-delete-flow' }"
 *   @confirm="deleteFlow"
 * />
 *
 * @example — error shown in the slot; the dialog stays open on failure
 * <ConfirmActionDialog
 *   v-model:visible="showDeleteModal"
 *   :title="$t('storage.deleteTitle')"
 *   :message="$t('storage.deleteMessage')"
 *   :loading="isDeleting"
 *   @confirm="deleteStorage"
 *   @cancel="deleteError = ''"
 * >
 *   <Message v-if="deleteError" severity="error" :closable="false">{{ deleteError }}</Message>
 * </ConfirmActionDialog>
 *
 * @example — typed confirmation gating the Confirm button
 * <ConfirmActionDialog
 *   v-model:visible="showResetModal"
 *   severity="warn"
 *   icon="warning"
 *   :title="$t('admin.resetDataModal.title')"
 *   :message="$t('admin.resetDataModal.message')"
 *   :confirm-props="{ disabled: typedName !== bot.name }"
 *   :loading="resetting"
 *   @confirm="resetBot"
 * >
 *   <InputText v-model="typedName" autofocus class="w-full" />
 * </ConfirmActionDialog>
 *
 * @prop {Boolean} visible      - Dialog visibility; use as `v-model:visible`.
 * @prop {String}  title        - Already-translated title; the dialog's accessible name.
 * @prop {String}  message      - Already-translated message; the `<p>` renders only when non-empty.
 * @prop {String}  confirmLabel - Already-translated Confirm label; falls back to `common.confirm`.
 * @prop {String}  cancelLabel  - Already-translated Cancel label; falls back to `common.cancel`.
 * @prop {String}  severity     - 'danger' (default) | 'warn' | 'primary'. Sets the Confirm
 *                                button severity and the header icon tint.
 * @prop {String}  icon         - Material icon name shown before the title (optional).
 * @prop {Boolean} loading      - Request in progress: spinner on Confirm, dialog locked open.
 * @prop {Object}  confirmProps - Extra attributes for the Confirm Button (e.g. `id`, `disabled`).
 *                                `disabled` is OR-ed with `loading`. Cannot override label,
 *                                severity, loading or autofocus.
 * @prop {Object}  cancelProps  - Extra attributes for the Cancel Button (e.g. `id`).
 *                                Cannot override label, severity, disabled or autofocus.
 *
 * @event update:visible - (false) on Cancel / X / Escape / mask click, never while loading.
 * @event after-hide - Passed through to the inner Dialog (single root, no inheritAttrs: false): fires after the
 *   leave transition, the right moment to reset state the message still shows while fading (see DeploySnapshots).
 * @event confirm        - Confirm clicked. The dialog stays open; the parent closes it.
 * @event cancel         - Emitted together with `update:visible` (false).
 *
 * @slot default - Extra content below the message (error `<Message>`, typed-confirmation input…).
 */

import { computed, useId } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  /** Dialog visibility; use as `v-model:visible`. */
  visible: {
    type: Boolean,
    default: false
  },
  /** Already-translated title, used as the dialog's accessible name. */
  title: {
    type: String,
    default: ''
  },
  /** Already-translated message. Its paragraph renders only when non-empty. */
  message: {
    type: String,
    default: ''
  },
  /** Already-translated Confirm label. Empty falls back to `common.confirm`. */
  confirmLabel: {
    type: String,
    default: ''
  },
  /** Already-translated Cancel label. Empty falls back to `common.cancel`. */
  cancelLabel: {
    type: String,
    default: ''
  },
  /** Confirm button severity, also used to tint the header icon. */
  severity: {
    type: String,
    default: 'danger',
    validator: (v) => ['danger', 'warn', 'primary'].includes(v)
  },
  /** Material icon name shown before the title. No icon when empty. */
  icon: {
    type: String,
    default: ''
  },
  /** Request in progress: Confirm shows its spinner and the dialog can't be closed. */
  loading: {
    type: Boolean,
    default: false
  },
  /** Extra attributes for the Confirm Button (e.g. `id`, `disabled` for a typed
   * confirmation). Label, severity, loading and autofocus are owned by this component. */
  confirmProps: {
    type: Object,
    default: () => ({})
  },
  /** Extra attributes for the Cancel Button (e.g. `id`). Label, severity and
   * disabled are owned by this component. */
  cancelProps: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['update:visible', 'confirm', 'cancel'])

/** Ids linking the dialog to its title (accessible name) and message (description). */
const titleId = useId()
const messageId = `${titleId}-message`

const resolvedConfirmLabel = computed(() => props.confirmLabel || t('common.confirm'))
const resolvedCancelLabel = computed(() => props.cancelLabel || t('common.cancel'))

/** PrimeVue Button has no 'primary' severity: primary is its default (no severity). */
const confirmSeverity = computed(() => (props.severity === 'primary' ? undefined : props.severity))

/** The consumer disabled Confirm (e.g. a typed confirmation that doesn't match yet). */
const confirmBlocked = computed(() => !!props.confirmProps?.disabled)

/** Initial focus (the Dialog focuses the first `autofocus` in the footer, then
 * header, then content). Danger focuses Cancel so Enter can't run the destructive
 * action; warn / primary focus Confirm. A blocked Confirm turns both off, leaving
 * focus to an `autofocus` element in the slot. */
const cancelAutofocus = computed(() => props.severity === 'danger' && !confirmBlocked.value)
const confirmAutofocus = computed(() => props.severity !== 'danger' && !confirmBlocked.value)

/** Ignored while loading: the explicit `:disabled` already blocks the button, this
 * also covers any click or Enter that still reaches the handler. */
function onConfirm() {
  if (props.loading) return
  emit('confirm')
}

/** Cancel, X, Escape and mask click all land here. Ignored while loading (the lock
 * holds even if a close request gets past the Dialog props) and once `visible` is
 * false (a second Esc / mask click during the leave transition). */
function requestClose() {
  if (props.loading || !props.visible) return
  emit('cancel')
  emit('update:visible', false)
}

/** Dialog only emits `update:visible` with false, on X / Escape / mask click. */
function onDialogVisibleChange(value) {
  if (!value) requestClose()
}
</script>

<template>
  <Dialog
    :visible="visible"
    modal
    role="alertdialog"
    :aria-labelledby="titleId"
    :aria-describedby="message ? messageId : undefined"
    :closable="!loading"
    :close-on-escape="!loading"
    :dismissable-mask="!loading"
    :draggable="false"
    :style="{ width: '28rem' }"
    :breakpoints="{ '640px': '95vw' }"
    @update:visible="onDialogVisibleChange"
  >
    <!-- Custom header so the icon sits before the title. The slot's `class` keeps
         PrimeVue's title styling; the id gives the dialog its accessible name
         (PrimeVue's own title id only exists with the `header` prop). -->
    <template #header="{ class: titleClass }">
      <div class="flex items-center gap-2 min-w-0">
        <span
          v-if="icon"
          class="material-icons shrink-0 select-none"
          :class="`confirm-action-dialog__icon--${severity}`"
          style="font-size: 1.5rem"
          aria-hidden="true"
        >{{ icon }}</span>
        <span :id="titleId" :class="titleClass">{{ title }}</span>
      </div>
    </template>

    <div class="flex flex-col gap-4">
      <p v-if="message" :id="messageId" class="text-gray-300">{{ message }}</p>
      <slot />
    </div>

    <template #footer>
      <!-- Attribute objects are bound first so the explicit bindings below win. -->
      <Button
        v-bind="cancelProps"
        severity="secondary"
        :label="resolvedCancelLabel"
        :disabled="loading"
        :autofocus="cancelAutofocus"
        @click="requestClose"
      />
      <!-- Explicit :disabled is required: PrimeVue Button re-spreads raw $attrs
           after its own `disabled || loading`, so a `disabled: false` coming from
           confirmProps would otherwise re-enable the button mid-request. -->
      <Button
        v-bind="confirmProps"
        :severity="confirmSeverity"
        :label="resolvedConfirmLabel"
        :loading="loading"
        :disabled="loading || confirmBlocked"
        :autofocus="confirmAutofocus"
        @click="onConfirm"
      />
    </template>
  </Dialog>
</template>

<style scoped>
/* Header icon tint per severity: the same -400 steps FieldHint.vue uses for its
   error / warning text, readable on the gray-800 dialog surface. */
.confirm-action-dialog__icon--danger {
  color: var(--p-red-400);
}

.confirm-action-dialog__icon--warn {
  color: var(--p-amber-400);
}

.confirm-action-dialog__icon--primary {
  color: var(--p-primary-400);
}
</style>
